import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { sprintf } from "sprintf-js";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import SchedulesService from "../../../../services/campus/schedules.service";
import EnrollmentPrompt from "../../../management/enrollments/enrollmentPrompt";
import { UserPrompt } from "../../../../components/sectionContainer";
import { enrollingSet, getAccessPermissions, isEmpty, weekDays } from "../../../../common";
import { FilaHorario } from "./interfaces";

/*
	«Actualizar Matrícula» del horario: la lista de inscritos de siempre (EnrollmentPrompt). La pantalla vieja de Horarios la
	armaba con el horario completo del v1 (curso, módulo con kits, sesiones); aquí se pide ese mismo horario por su código y se le
	da la misma forma, para no tocar EnrollmentPrompt.
*/
const http = HttpManager.getInstance();

const armarHorario = (schedule: any): any => {
	const hours: any = {};
	const weekSessions: string[] = [];
	(schedule.sessions || []).forEach((session: any) => {
		const startMinutes = session.start_hours % 10;
		const startHours = (session.start_hours - startMinutes) / 10;
		const endMinutes = session.end_hours % 10;
		const endHours = (session.end_hours - endMinutes) / 10;
		weekSessions.push(weekDays[session.week_day].substr(0, 2) + sprintf(" %02d:%02d-%02d:%02d", startHours, startMinutes * 6, endHours, endMinutes * 6));
		for (let h = session.start_hours; h < session.end_hours; h += 5) {
			hours[sprintf("DH%d%03d", session.week_day, h)] = true;
		}
	});
	const kitRef: any = {};
	const mentorRef: any = {};
	const mentors = (schedule.level.mentors || []).map((mentor: any) => {
		const idx = "M_" + mentor.id;
		mentorRef[idx] = {};
		(mentor.sessions || []).forEach((session: any) => {
			for (let h = session.start_hours; h < session.end_hours; h += 5) {
				mentorRef[idx][sprintf("DH%d%03d", session.week_day, h)] = session.schedule_id;
			}
		});
		return { ...mentor, label: mentor.first_name + " " + mentor.last_name };
	});
	(schedule.level.kits || []).forEach((kit: any) => { kitRef["K_" + kit.id] = kit; });
	return {
		...schedule,
		hours,
		weekSessions,
		period: { ...schedule.period, label: schedule.period?.name },
		classroom: { ...schedule.classroom, label: schedule.classroom?.name },
		mentor: isEmpty(schedule.mentor) ? null : { ...schedule.mentor, label: schedule.mentor.first_name + " " + schedule.mentor.last_name },
		level: { ...schedule.level, kitRef, mentorRef, mentors }
	};
};

interface Props {
	fila: FilaHorario;
	campusId: number;
	onCerrar: () => void;
	/** Algo cambió en las matrículas: la lista se vuelve a pedir. */
	onCambio: () => void;
	onError: (texto: string) => void;
}

const ActualizarMatricula: React.FC<Props> = ({ fila, campusId, onCerrar, onCambio, onError }) => {
	const perfil: any = useSelector((state: RootState) => (state as any).userProfile?.value);
	const campusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const currentUserCampus: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const basicInfo: any = useSelector((state: RootState) => state.basicInfo.value);
	const allowance = getAccessPermissions(perfil, "enrollments");
	const [seleccion, setSeleccion] = useState<any>(null);
	const [usuario, setUsuario] = useState<any>(null);

	useEffect(() => {
		let vigente = true;
		const fallo = (t: string) => { if (vigente) { onError(t); onCerrar(); } };
		(async () => {
			try {
				const r: any = await SchedulesService.searchSchedules({ q: fila.code, statusId: -1, owedKits: 2 }, 1);
				const lista: any[] = Array.isArray(r.data) ? r.data : (r.data?.data || []);
				const encontrado = lista.find((s: any) => s.id === fila.id);
				if (!encontrado) { fallo("El horario no pudo abrirse para actualizar matrículas."); return; }
				const horario = armarHorario(encontrado);
				const m = await http.enrollmentService.searchEnrollments(campusId, fila.id);
				if (!vigente) { return; }
				setSeleccion(enrollingSet(horario.level.course, horario.level, horario, m.data, campusRef[currentUserCampus].company_id, basicInfo.articleTypeRef));
			} catch (e) {
				console.error(e);
				fallo("La lista de matrículas no pudo ser extraída");
			}
		})();
		return () => { vigente = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fila.id]);

	if (!seleccion) { return null; }
	if (usuario) { return <UserPrompt promptCloseFn={() => setUsuario(null)} selectedUser={usuario} />; }
	return <EnrollmentPrompt closeFn={onCerrar} selectedEnrollment={seleccion} profileViewingFn={setUsuario} allowance={allowance} afterCloseFn={onCambio} />;
};

export default ActualizarMatricula;
